import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getImageUrl } from '../../utils/imageUtils';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Toast';
import { storeAIImage, fetchMedia } from '../media/mediaSlice';
import VariantMatrix from '../../components/VariantMatrix';
import VariantBuilderDrawer from './VariantBuilderDrawer';
import ReceiveStockSidebar from './ReceiveStockSidebar';
import MarkupField from '../../components/MarkupField';
import { selectCategories } from '../categories/categoriesSlice';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { videoLimitForTier, digitalLimitForTier, formatBytes, VIDEO_ACCEPT, DIGITAL_ACCEPT } from './mediaLimits';
import axios from '../../axiosConfig';
import { apiFetch } from '../../utils/api';
import {
  FileText,
  DollarSign,
  Image,
  Download,
  Calendar,
  Layers,
  Sparkles,
  Eye,
  Code as CodeIcon,
  PackagePlus,
  Boxes,
  Truck,
  Mail,
  Lock,
  Send
} from 'lucide-react';
import './ProductForm.css';

/**
 * Belt-and-braces frontend HTML scrub. Backend sanitises before sending,
 * so this is only defence in depth for content the merchant may paste.
 * Drops script tags, on* attributes, and javascript: URLs.
 */
const sanitizeDescriptionHtml = (html = '') => {
  if (!html) return '';
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/ on[a-z]+="[^"]*"/gi, '')
    .replace(/ on[a-z]+='[^']*'/gi, '')
    .replace(/javascript:/gi, '');
};

// Unit of measure options for unit-based selling
const UNIT_OPTIONS = [
  { value: 'PIECE', label: 'Piece (pc)', allowDecimal: false },
  { value: 'KG', label: 'Kilogram (kg)', allowDecimal: true },
  { value: 'G', label: 'Gram (g)', allowDecimal: true },
  { value: 'LB', label: 'Pound (lb)', allowDecimal: true },
  { value: 'OZ', label: 'Ounce (oz)', allowDecimal: true },
  { value: 'METER', label: 'Meter (m)', allowDecimal: true },
  { value: 'YARD', label: 'Yard (yd)', allowDecimal: true },
  { value: 'FT', label: 'Foot (ft)', allowDecimal: true },
  { value: 'CM', label: 'Centimeter (cm)', allowDecimal: true },
  { value: 'INCH', label: 'Inch (in)', allowDecimal: true },
  { value: 'LITER', label: 'Liter (L)', allowDecimal: true },
  { value: 'ML', label: 'Milliliter (ml)', allowDecimal: true },
  { value: 'GALLON', label: 'Gallon (gal)', allowDecimal: true },
  { value: 'SQM', label: 'Square Meter (sqm)', allowDecimal: true },
  { value: 'SQFT', label: 'Square Foot (sqft)', allowDecimal: true },
];

const ProductForm = ({
  product = null,
  onSubmit,
  onCancel,
  loading = false,
  /**
   * `tabs` (default) — legacy tabbed layout used by the Products list sidebar.
   * `sections` — new full-page layout: tab nav is hidden, every tab content
   *   block renders as a long-scrolling section card and the bottom action
   *   bar is suppressed so the page chrome (sticky topbar) owns Publish.
   */
  layout = 'tabs',
  /** Stable id for the <form>, used by the page-level Publish button. */
  formId = 'product-form',
}) => {
  const [activeTab, setActiveTab] = useState('basic');
  const isSections = layout === 'sections';

  // In sections mode, every tab content block is unconditionally visible.
  // Wrap each block in a section card with an anchor id for scroll-spy.
  const sectionVisible = (id) => isSections || activeTab === id;

  // Basic Information
  const [name, setName] = useState(product?.name || '');
  const [slug, setSlug] = useState(product?.slug || '');
  const [description, setDescription] = useState(product?.description || '');
  const [descPreview, setDescPreview] = useState(false);
  const [category, setCategory] = useState(product?.category || '');
  const [categoryId, setCategoryId] = useState(product?.categoryId || '');
  const [dynamicCategories, setDynamicCategories] = useState([]);
  const [tags, setTags] = useState(product?.tags?.join(', ') || '');

  // Pricing & Inventory
  const [price, setPrice] = useState(product?.price || '');
  const [compareAtPrice, setCompareAtPrice] = useState(product?.compareAtPrice || '');
  const [costPrice, setCostPrice] = useState(product?.costPrice || '');
  // Phase 4.A.6 polish — per-product markup % override. Falls back to
  // category.markupPercent → store.defaultMarkupPercent on save when null/empty.
  const [markupPercent, setMarkupPercent] = useState(
    product?.markupPercent != null ? String(product.markupPercent) : ''
  );
  // Store-wide pricing defaults — fetched once when the form mounts so
  // the MarkupField can show "Inherits X% from store" instead of an empty box.
  const [storePricingDefaults, setStorePricingDefaults] = useState(null);
  // Phase 4.A.6 — `quantity` is editable on initial create only.
  // Once a product exists, Quantity becomes a read-only display of
  // the maintained aggregate and any new units come via the
  // ReceiveStockSidebar (which creates a ProductBatch row).
  const [quantity, setQuantity] = useState(product?.quantity || '');
  // Live aggregate shown in the read-only display — bumps after a
  // successful Receive Stock save so the merchant sees the new total
  // without a full reload.
  const [displayQuantity, setDisplayQuantity] = useState(product?.quantity ?? 0);
  const [receiveStockOpen, setReceiveStockOpen] = useState(false);
  const isEditingExisting = Boolean(product?.id);
  const [supplierContext, setSupplierContext] = useState(null);
  const [supplierLoading, setSupplierLoading] = useState(false);
  const [supplierSaving, setSupplierSaving] = useState(false);
  const [supplierNotifying, setSupplierNotifying] = useState(false);
  const [supplierForm, setSupplierForm] = useState({
    supplierId: '',
    name: '',
    contactName: '',
    email: '',
    phone: '',
    whatsappNumber: '',
    reorderQuantity: '',
    autoNotify: false
  });
  const [sku, setSku] = useState(product?.sku || '');
  // Track whether the merchant has hand-edited the SKU. Once true,
  // auto-generation on name change stops touching it — they own it.
  const [skuManuallyEdited, setSkuManuallyEdited] = useState(Boolean(product?.sku));
  const [lowStockThreshold, setLowStockThreshold] = useState(product?.lowStockThreshold ?? '');
  // Finance — optional expiry date stored as ISO date (YYYY-MM-DD for
  // <input type="date">). Backend accepts null/'' for non-perishables.
  const [expiryDate, setExpiryDate] = useState(
    product?.expiryDate ? String(product.expiryDate).slice(0, 10) : ''
  );

  // Unit-Based Selling
  const [sellByUnit, setSellByUnit] = useState(product?.sellByUnit || false);
  const [unitOfMeasure, setUnitOfMeasure] = useState(product?.unitOfMeasure || 'PIECE');
  const [minOrderQuantity, setMinOrderQuantity] = useState(product?.minOrderQuantity || '');
  const [stepIncrement, setStepIncrement] = useState(product?.stepIncrement || '');

  // Media
  const [imageUrl, setImageUrl] = useState(product?.imageUrl || product?.primaryImage || '');
  const [images, setImages] = useState(product?.images?.join(', ') || product?.gallery?.join(', ') || '');
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [mediaType, setMediaType] = useState('image');

  // Product Type & Status
  const [productType, setProductType] = useState(product?.productType?.toLowerCase() || 'physical');

  // Phase 4.A.6 polish — fetch store-wide pricing defaults once so the
  // MarkupField can show inherited values instead of an empty input.
  // The hierarchy resolved on display: product override → category
  // markup → store default → null.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get('/merchants/pricing-defaults');
        if (!cancelled) setStorePricingDefaults(data);
      } catch (err) {
        console.warn('[ProductForm] pricing-defaults fetch failed', err?.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const allCategories = useSelector(selectCategories);
  const inheritedMarkup = (() => {
    // Tier 2: category override
    if (categoryId) {
      const cat = (allCategories || []).find((c) => c.id === categoryId);
      if (cat?.markupPercent != null) {
        return { value: Number(cat.markupPercent), from: 'category' };
      }
    }
    // Tier 3: store default
    if (storePricingDefaults?.defaultMarkupPercent != null) {
      return { value: Number(storePricingDefaults.defaultMarkupPercent), from: 'store' };
    }
    return { value: null, from: null };
  })();

  // Phase 4.A.6 polish — the markup % that should actually apply right
  // now. Product override beats inherited; null means no markup defined
  // anywhere. Drives the auto-fill on cost change + the "Apply markup"
  // chip near the Price input.
  const effectiveMarkupPercent = (() => {
    if (markupPercent !== '' && markupPercent != null) {
      const m = parseFloat(markupPercent);
      if (!isNaN(m)) return m;
    }
    return inheritedMarkup.value;
  })();
  const markupSuggestedPrice = (() => {
    const c = parseFloat(costPrice);
    if (!Number.isFinite(c) || c <= 0) return null;
    if (effectiveMarkupPercent == null) return null;
    return Number((c * (1 + effectiveMarkupPercent / 100)).toFixed(2));
  })();
  const priceMatchesMarkup =
    markupSuggestedPrice != null &&
    parseFloat(price) === markupSuggestedPrice;

  // Notify the wrapping page (ProductFormPage) when productType changes so
  // its left-rail nav can show/hide Digital / Appointments. Sections layout
  // only — tabbed layout already has its own conditional tab list.
  useEffect(() => {
    if (!isSections) return;
    const node = document.querySelector('.pfs-page');
    if (!node) return;
    node.dispatchEvent(new CustomEvent('pfs:product-type', {
      detail: { productType },
      bubbles: false,
    }));
  }, [productType, isSections]);
  const [status, setStatus] = useState(product?.status?.toUpperCase() || 'DRAFT');
  const [featured, setFeatured] = useState(product?.featured || false);

  // Storefront Display Flags
  const [bestSeller, setBestSeller] = useState(product?.bestSeller || product?.isBestSeller || false);
  const [trending, setTrending] = useState(product?.trending || product?.isTrending || false);
  const [newArrival, setNewArrival] = useState(product?.newArrival || product?.isNewArrival || false);

  // Appointment Settings
  // Merge defaults UNDER the persisted object — Product.appointmentSettings
  // defaults to `{}` in Prisma, so a plain `|| defaults` keeps the empty
  // object (truthy) and never fills array keys; UI then explodes when
  // it reaches `availableDays.includes(...)`.
  const [appointmentSettings, setAppointmentSettings] = useState({
      duration: 60,
      bufferTime: 15,
      requiresApproval: false,
      location: '', // Meeting location (Zoom/Meet link or physical address)
      availableDays: [],
      availableTimeSlots: [],
      cancellationPolicy: {
        allowCancellation: true,
        minHoursNotice: 24
      },
      calendarIntegration: {
        enabled: false,
        provider: null, // 'google' or 'calendly'
        calendarId: '',
        calendlyLink: ''
      },
      // Spread persisted values on top of the safe defaults above.
      ...(product?.appointmentSettings || {})
    }
  );

  // Digital Product Settings
  const [digitalSettings, setDigitalSettings] = useState(
    product?.digitalSettings || {
      downloadUrl: '',        // Internal file URL (S3/storage)
      externalUrl: '',        // External link (Google Drive, Dropbox, etc.)
      maxDownloads: null,     // null = unlimited
      expirationDays: null,   // null = never expires
      requiresLicenseKey: false,
      deliveryMethod: 'email' // email, instant
    }
  );

  // Free/Paid appointment toggle
  const [isFreeAppointment, setIsFreeAppointment] = useState(
    (product?.price === 0 || product?.price === '0' || !product?.price) && productType === 'appointment'
  );

  const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  // Variations - New Variant Matrix approach
  const [hasVariations, setHasVariations] = useState(product?.hasVariations || false);
  const [variantDrawerOpen, setVariantDrawerOpen] = useState(false);
  const [variantMatrix, setVariantMatrix] = useState(() => {
    // Initialize from product's variantMatrix or convert legacy format
    if (product?.variantMatrix?.attributes) {
      return product.variantMatrix;
    }
    // Check if variations is already in new format
    if (product?.variations?.attributes) {
      return product.variations;
    }
    return { attributes: [], variants: [] };
  });
  const [attributeLimit, setAttributeLimit] = useState(null); // Fetched from merchant tier

  // AI Generation
  const [generating, setGenerating] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [aiError, setAiError] = useState(null);

  // Redux
  const dispatch = useDispatch();
  const { media } = useSelector(state => state.media);
  const toast = useToast();

  // Image upload states
  const [primaryImageFile, setPrimaryImageFile] = useState(null);

  // Video upload (tier-gated via `media.video`). Stored on the product via
  // digitalSettings.video.url so we don't need a schema migration.
  const [videoUrl, setVideoUrl] = useState(product?.digitalSettings?.video?.url || '');
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoError, setVideoError] = useState('');
  const [merchantTier, setMerchantTier] = useState('FREE');
  // Video limit (in bytes) sourced from the Tier row's `maxVideoSizeMb`
  // via /merchants/me — admin-editable. Falls back to the static mirror
  // only if the API doesn't return tierLimits (older backend) or we
  // haven't received the response yet.
  const [videoLimitBytes, setVideoLimitBytes] = useState(null);
  const { hasAccess: hasVideoAccess, loading: videoAccessLoading } = useFeatureAccess('media.video');
  const videoLimit = videoLimitBytes != null ? videoLimitBytes : videoLimitForTier(merchantTier);

  // Digital-asset upload (tier-gated via `media.digital`). The uploaded
  // file URL still lands on digitalSettings.downloadUrl so the existing
  // mobile + backend reads keep working unchanged; we just track a tiny
  // metadata blob locally so we can show filename + size in the filled
  // state without re-deriving from the URL.
  //
  // Backcompat: if a pre-existing product has digitalSettings.downloadUrl
  // but no `file` metadata (created before B5b), fall back to deriving
  // a minimal meta blob from the URL so the filled state still renders
  // and the merchant can replace/remove rather than seeing a stale URL.
  const initialDigitalFile = product?.digitalSettings?.file
    || (product?.digitalSettings?.downloadUrl
      ? {
          url: product.digitalSettings.downloadUrl,
          originalName: product.digitalSettings.downloadUrl.split('/').pop() || 'Uploaded file',
          size: 0,
          mimeType: '',
        }
      : null);
  const [digitalFileMeta, setDigitalFileMeta] = useState(initialDigitalFile);
  const [digitalUploading, setDigitalUploading] = useState(false);
  const [digitalError, setDigitalError] = useState('');
  const [digitalLimitBytes, setDigitalLimitBytes] = useState(null);
  const { hasAccess: hasDigitalAccess, loading: digitalAccessLoading } = useFeatureAccess('media.digital');
  const digitalLimit = digitalLimitBytes != null ? digitalLimitBytes : digitalLimitForTier(merchantTier);
  const {
    hasAccess: hasSupplierReorderAccess,
    loading: supplierAccessLoading
  } = useFeatureAccess('inventory.supplier_reorder');

  // Fetch the merchant's effective tier + tier-resolved media limits once
  // so the video and digital subsections render the per-tier size hint
  // with the admin-controlled value (no extra round-trip per render).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get('/merchants/me');
        if (cancelled) return;
        const tier = data?.user?.effectiveTier || data?.user?.merchantTier || 'FREE';
        setMerchantTier(tier);
        const videoMb = data?.user?.tierLimits?.maxVideoSizeMb;
        if (typeof videoMb === 'number') {
          setVideoLimitBytes(videoMb * 1024 * 1024);
        }
        const digitalMb = data?.user?.tierLimits?.maxDigitalAssetSizeMb;
        if (typeof digitalMb === 'number') {
          setDigitalLimitBytes(digitalMb * 1024 * 1024);
        }
      } catch {
        // Silent — falls back to the static mirrors.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!isEditingExisting || supplierAccessLoading || !hasSupplierReorderAccess) return undefined;

    let cancelled = false;
    const loadSupplierSetup = async () => {
      setSupplierLoading(true);
      try {
        const response = await apiFetch(`/merchant/suppliers/reorder/products/${product.id}`);
        const data = await response.json();
        if (cancelled) return;

        if (!response.ok || !data.success) {
          throw new Error(data.error || 'Failed to load supplier setup');
        }

        setSupplierContext(data);
        const link = data.supplierLink;
        const supplier = link?.supplier;
        if (supplier) {
          setSupplierForm({
            supplierId: supplier.id || '',
            name: supplier.name || '',
            contactName: supplier.contactName || '',
            email: supplier.email || '',
            phone: supplier.phone || '',
            whatsappNumber: supplier.whatsappNumber || '',
            reorderQuantity: link.reorderQuantity ?? '',
            autoNotify: link.autoNotify === true
          });
        }
      } catch (error) {
        if (!cancelled) {
          console.error('[ProductForm] supplier setup load failed:', error);
        }
      } finally {
        if (!cancelled) setSupplierLoading(false);
      }
    };

    loadSupplierSetup();
    return () => { cancelled = true; };
  }, [isEditingExisting, product?.id, supplierAccessLoading, hasSupplierReorderAccess]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Fetch media library on component mount
  useEffect(() => {
    dispatch(fetchMedia({ params: {} }));
  }, [dispatch]);

  // Fetch categories on mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await apiFetch('/merchants/categories?tree=true');
        const data = await response.json();
        if (response.ok && data.data) {
          setDynamicCategories(data.data);
        }
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    loadCategories();
  }, []);

  // Update form fields when product prop changes (for editing)
  useEffect(() => {
    if (product) {
      console.log('ProductForm: Loading product for editing:', {
        id: product.id,
        bestSeller: product.bestSeller,
        trending: product.trending,
        newArrival: product.newArrival,
        featured: product.featured
      });

      setName(product.name || '');
      setSlug(product.slug || '');
      setDescription(product.description || '');
      setCategory(product.category || '');
      setCategoryId(product.categoryId || '');
      setTags(product.tags?.join(', ') || '');
      setPrice(product.price || '');
      setCompareAtPrice(product.compareAtPrice || '');
      setCostPrice(product.costPrice || '');
      setQuantity(product.quantity || '');
      setDisplayQuantity(product.quantity ?? 0);
      setMarkupPercent(product.markupPercent != null ? String(product.markupPercent) : '');
      setSku(product.sku || '');
      setLowStockThreshold(product.lowStockThreshold ?? '');
      setExpiryDate(product.expiryDate ? String(product.expiryDate).slice(0, 10) : '');
      setImageUrl(product.imageUrl || product.primaryImage || '');
      setImages(product.images?.join(', ') || product.gallery?.join(', ') || '');
      setProductType(product.productType?.toLowerCase() || 'physical');
      setStatus(product.status?.toUpperCase() || 'DRAFT');
      setFeatured(product.featured || false);
      setBestSeller(product.bestSeller || product.isBestSeller || false);
      setTrending(product.trending || product.isTrending || false);
      setNewArrival(product.newArrival || product.isNewArrival || false);
      // Same merge-under-defaults trick as the initial useState — partial
      // {} from the DB has to inherit array keys or the appointment tab
      // crashes on availableDays.includes.
      setAppointmentSettings({
        duration: 60,
        bufferTime: 15,
        requiresApproval: false,
        location: '',
        availableDays: [],
        availableTimeSlots: [],
        cancellationPolicy: {
          allowCancellation: true,
          minHoursNotice: 24
        },
        calendarIntegration: {
          enabled: false,
          provider: null,
          calendarId: '',
          calendlyLink: ''
        },
        ...(product.appointmentSettings || {})
      });
      setIsFreeAppointment((product.price === 0 || product.price === '0' || !product.price) && product.productType === 'appointment');
      setDigitalSettings(product.digitalSettings || {
        downloadUrl: '',
        externalUrl: '',
        maxDownloads: null,
        expirationDays: null,
        requiresLicenseKey: false,
        deliveryMethod: 'email'
      });
      // Re-derive the digital-file meta when product (re)loads. Mirrors
      // the lazy fallback in the useState initializer so async-loaded
      // products show their existing file in the filled state.
      setDigitalFileMeta(
        product.digitalSettings?.file
          || (product.digitalSettings?.downloadUrl
            ? {
                url: product.digitalSettings.downloadUrl,
                originalName: product.digitalSettings.downloadUrl.split('/').pop() || 'Uploaded file',
                size: 0,
                mimeType: '',
              }
            : null)
      );
      setHasVariations(product.hasVariations || false);
      // Set variant matrix - check for new format first, then initialize empty
      if (product?.variantMatrix?.attributes) {
        setVariantMatrix(product.variantMatrix);
      } else if (product?.variations?.attributes) {
        setVariantMatrix(product.variations);
      } else {
        setVariantMatrix({ attributes: [], variants: [] });
      }
    }
  }, [product]);

  // Fetch merchant's variation attribute limit from tier
  useEffect(() => {
    const fetchAttributeLimit = async () => {
      try {
        const response = await axios.get('/merchants/limits');
        if (response.data?.variationAttributes?.limit !== undefined) {
          setAttributeLimit(response.data.variationAttributes.limit);
        }
      } catch (error) {
        console.error('Failed to fetch attribute limit:', error);
        // Default to FREE tier limit if fetch fails
        setAttributeLimit(2);
      }
    };
    fetchAttributeLimit();
  }, []);

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  /**
   * Auto-generate a SKU from the product name + a short random suffix.
   * Pattern matches Shopify/Square: <first-3-word-tokens>-<4 chars>.
   * Example: "Premium Shirt Wool" → "PRE-SHI-WOO-K3D9"
   *
   * The suffix dodges collisions when two products share a similar
   * leading word. The merchant can still overwrite it manually — this
   * just gives them a useful starting point instead of an empty field.
   */
  const generateSku = (name) => {
    if (!name || !name.trim()) return '';
    const tokens = name.trim().toUpperCase().split(/[^A-Z0-9]+/).filter(Boolean);
    const prefix = tokens.slice(0, 3).map((t) => t.slice(0, 3)).join('-');
    let suffix = '';
    for (let i = 0; i < 4; i++) {
      suffix += 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'[Math.floor(Math.random() * 31)];
    }
    return prefix ? `${prefix}-${suffix}` : suffix;
  };

  const handleNameChange = (e) => {
    const newName = e.target.value;
    setName(newName);
    // Only auto-generate slug + SKU for NEW products and only when
    // the merchant hasn't already typed their own. Editing an
    // existing product never overwrites — that would change links
    // that may already be in use.
    if (!product?.id) {
      setSlug(generateSlug(newName));
      if (!skuManuallyEdited) {
        setSku(generateSku(newName));
      }
    }
  };

  // Variant Matrix change handler
  const handleVariantMatrixChange = (newMatrix) => {
    setVariantMatrix(newMatrix);
    // Auto-enable variations if attributes are added
    if (newMatrix.attributes && newMatrix.attributes.length > 0) {
      setHasVariations(true);
    } else {
      setHasVariations(false);
    }
  };

  const supplierThreshold = lowStockThreshold === '' || lowStockThreshold == null
    ? 5
    : Number(lowStockThreshold);
  const supplierCurrentStock = Number(
    supplierContext?.product?.quantity ?? displayQuantity ?? quantity ?? 0
  );
  const supplierIsLowStock = supplierCurrentStock <= supplierThreshold;

  const handleSupplierFieldChange = (field, value) => {
    setSupplierForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSupplierSelect = (supplierId) => {
    const selected = supplierContext?.suppliers?.find((supplier) => supplier.id === supplierId);
    if (!selected) {
      setSupplierForm((prev) => ({
        ...prev,
        supplierId: '',
        name: '',
        contactName: '',
        email: '',
        phone: '',
        whatsappNumber: ''
      }));
      return;
    }

    setSupplierForm((prev) => ({
      ...prev,
      supplierId: selected.id,
      name: selected.name || '',
      contactName: selected.contactName || '',
      email: selected.email || '',
      phone: selected.phone || '',
      whatsappNumber: selected.whatsappNumber || ''
    }));
  };

  const reloadSupplierContext = async () => {
    if (!product?.id) return null;
    const response = await apiFetch(`/merchant/suppliers/reorder/products/${product.id}`);
    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Failed to load supplier setup');
    }
    setSupplierContext(data);
    return data;
  };

  const saveSupplierSetup = async () => {
    if (!product?.id) {
      toast.error('Save the product before linking a supplier');
      return;
    }
    if (!supplierForm.name.trim()) {
      toast.error('Supplier name is required');
      return;
    }
    if (!supplierForm.email.trim()) {
      toast.error('Supplier email is required for email reorder requests');
      return;
    }

    setSupplierSaving(true);
    try {
      const response = await apiFetch(`/merchant/suppliers/reorder/products/${product.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          supplierId: supplierForm.supplierId || undefined,
          supplier: {
            name: supplierForm.name,
            contactName: supplierForm.contactName,
            email: supplierForm.email,
            phone: supplierForm.phone,
            whatsappNumber: supplierForm.whatsappNumber
          },
          reorderQuantity: supplierForm.reorderQuantity === '' ? null : Number(supplierForm.reorderQuantity),
          autoNotify: false
        })
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to save supplier setup');
      }

      const refreshed = await reloadSupplierContext();
      const supplier = refreshed?.supplierLink?.supplier;
      if (supplier) {
        setSupplierForm((prev) => ({
          ...prev,
          supplierId: supplier.id,
          name: supplier.name || prev.name,
          contactName: supplier.contactName || '',
          email: supplier.email || '',
          phone: supplier.phone || '',
          whatsappNumber: supplier.whatsappNumber || ''
        }));
      }
      toast.success('Supplier setup saved');
    } catch (error) {
      toast.error(error.message || 'Failed to save supplier setup');
    } finally {
      setSupplierSaving(false);
    }
  };

  const notifySupplierNow = async () => {
    if (!product?.id) return;
    if (!supplierForm.supplierId) {
      toast.error('Save supplier setup before sending a reorder email');
      return;
    }
    if (!supplierIsLowStock) {
      toast.error('This product has not reached its low-stock threshold');
      return;
    }

    setSupplierNotifying(true);
    try {
      const response = await apiFetch(`/merchant/suppliers/reorder/products/${product.id}/notify`, {
        method: 'POST',
        body: JSON.stringify({
          supplierId: supplierForm.supplierId,
          channel: 'EMAIL'
        })
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Supplier email could not be sent');
      }

      await reloadSupplierContext();
      toast.success('Supplier reorder email sent');
    } catch (error) {
      toast.error(error.message || 'Supplier email could not be sent');
    } finally {
      setSupplierNotifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error('Product name is required');
      return;
    }
    if (productType === 'appointment' && !isFreeAppointment && (!price || isNaN(parseFloat(price)))) {
      toast.error('Valid price is required for paid appointments');
      return;
    }
    if (productType !== 'appointment' && (!price || isNaN(parseFloat(price)))) {
      toast.error('Valid price is required');
      return;
    }
    if (!category.trim()) {
      toast.error('Category is required');
      return;
    }

    try {
      let finalImageUrl = imageUrl;
      let finalImages = images ? images.split(',').map(img => img.trim()).filter(Boolean) : [];

      if (primaryImageFile || galleryImages.length > 0) {
        const uploadedUrls = await uploadImages();
        if (uploadedUrls.length > 0) {
          finalImageUrl = uploadedUrls[0] || imageUrl;
          finalImages = [...uploadedUrls.slice(1), ...finalImages];
        }
      }

      const productData = {
        name,
        slug: product?.id ? (slug || product.slug) : (slug || generateSlug(name)),
        description: description && description.trim() ? description.trim() : undefined,
        price: parseFloat(price),
        compareAtPrice: compareAtPrice ? parseFloat(compareAtPrice) : undefined,
        costPrice: costPrice !== '' && costPrice != null ? parseFloat(costPrice) : undefined,
        // Phase 4.A.6 polish — per-product markup % override. null/''
        // means "inherit Store.defaultMarkupPercent".
        markupPercent: markupPercent === '' || markupPercent == null
          ? null
          : parseFloat(markupPercent),
        quantity: parseInt(quantity) || 0,
        sku: sku && sku.trim() ? sku.trim() : undefined,
        // Finance — null/'' means clear the value (productServicePrisma
        // handles both as "set to null"). Send the field unconditionally
        // so removing an expiry actually persists.
        expiryDate: expiryDate || null,
        lowStockThreshold: lowStockThreshold === '' || lowStockThreshold == null
          ? null
          : parseInt(lowStockThreshold, 10),
        category,
        categoryId: categoryId || undefined,
        tags: tags ? tags.split(',').map(tag => tag.trim()).filter(Boolean) : [],
        imageUrl: finalImageUrl || undefined,
        images: finalImages,
        primaryImage: finalImageUrl || undefined,
        gallery: finalImages,
        status,
        featured,
        bestSeller,
        trending,
        newArrival,
        productType,
        hasVariations,
        // Store variant matrix in the variations field (new format)
        variations: hasVariations ? variantMatrix : { attributes: [], variants: [] },
        // Unit-based selling fields
        sellByUnit,
        unitOfMeasure: unitOfMeasure || 'PIECE',
        minOrderQuantity: sellByUnit && minOrderQuantity ? parseFloat(minOrderQuantity) : null,
        stepIncrement: sellByUnit && stepIncrement ? parseFloat(stepIncrement) : null,
        ...(productType === 'appointment' && { appointmentSettings }),
        // digitalSettings always goes through so the product video (which is
        // valid for ANY product type) is persisted. Digital-only fields
        // inside it stay scoped to digital products via the form UI.
        // The uploaded file's metadata is also persisted under
        // digitalSettings.file so the UI can re-render filename + size on
        // edit without re-deriving from the URL; mobile + backend just
        // read digitalSettings.downloadUrl as before.
        digitalSettings: {
          ...(productType === 'digital' ? digitalSettings : {}),
          ...(videoUrl ? { video: { url: videoUrl } } : {}),
          ...(productType === 'digital' && digitalFileMeta ? { file: digitalFileMeta } : {}),
        },
      };

      console.log('ProductForm: Submitting product data with storefront flags:', {
        bestSeller,
        trending,
        newArrival,
        featured
      });

      onSubmit(productData);
    } catch (error) {
      console.error('Error preparing product data:', error);
      // Surface the real backend message (storage limit hit, unsupported
      // file type, multer field-count cap, etc.) instead of swallowing it
      // behind a generic string — merchants otherwise see a spinner that
      // just… never resolves when a publish silently fails.
      const backendMsg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message;
      const friendly = backendMsg && !/network error/i.test(backendMsg)
        ? backendMsg
        : 'Failed to upload images. Please try again.';
      toast.error(friendly);
      setAiError(friendly);
    }
  };

  const generateContent = async () => {
    if (!name.trim()) {
      setAiError('Please enter a product name first');
      return;
    }

    setGenerating(true);
    setAiError(null);

    try {
      const response = await axios.post('/ai/generate-description', {
        productName: name,
        category: category
      });

      if (response.data.description) {
        setDescription(response.data.description);
      }
      if (response.data.features && response.data.features.length > 0) {
        setTags(response.data.features.join(', '));
      }
    } catch (error) {
      console.error('Error generating content:', error);
      setAiError('Failed to generate content. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const generateImage = async () => {
    if (!name.trim()) {
      setAiError('Please enter a product name first');
      return;
    }

    setGeneratingImage(true);
    setAiError(null);

    try {
      const response = await axios.post('/ai/generate-image', {
        productName: name,
        category: category,
        description: description
      });

      const generatedImageUrl = response.data.imageUrl;

      const storedImage = await dispatch(storeAIImage({
        imageUrl: generatedImageUrl,
        prompt: `${name} ${category}`,
        alt: name,
        description: `AI-generated image for ${name}`
      })).unwrap();

      setImageUrl(storedImage.url);
    } catch (error) {
      console.error('Error generating image:', error);
      setAiError('Failed to generate image. Please try again.');
    } finally {
      setGeneratingImage(false);
    }
  };

  const handlePrimaryImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPrimaryImageFile(file);
      const previewUrl = URL.createObjectURL(file);
      setImageUrl(previewUrl);
    }
  };

  const handleGalleryImageUpload = (e) => {
    const files = Array.from(e.target.files);
    setGalleryImages(files);
  };

  /**
   * Upload a product video. Validated against the merchant's per-tier
   * cap client-side first (so we don't burn bandwidth on hopeless uploads),
   * then POSTed to `/upload/video`. The backend re-checks the cap, gates
   * the route by `media.video`, and stores via S3.
   */
  const handleVideoUpload = async (e) => {
    setVideoError('');
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input so picking the same file again still triggers onChange.
    e.target.value = '';

    if (!hasVideoAccess) {
      setVideoError('Product videos require a paid plan.');
      return;
    }
    if (videoLimit && file.size > videoLimit) {
      setVideoError(
        `Your plan allows videos up to ${formatBytes(videoLimit)}. This file is ${formatBytes(file.size)}.`
      );
      return;
    }

    setVideoUploading(true);
    try {
      const fd = new FormData();
      fd.append('video', file);
      const res = await axios.post('/upload/video', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.url) {
        setVideoUrl(res.data.url);
      } else {
        setVideoError('Upload completed but no URL was returned.');
      }
    } catch (err) {
      const data = err?.response?.data || {};
      setVideoError(data.error || 'Failed to upload video. Please try again.');
    } finally {
      setVideoUploading(false);
    }
  };

  const handleVideoRemove = () => {
    setVideoUrl('');
    setVideoError('');
  };

  /**
   * Digital file upload (PDF / EPUB / MOBI / ZIP / DOCX). Validated
   * against the per-tier cap client-side first, then POSTed to
   * `/upload/digital-asset`. Backend re-checks the cap, gates the route
   * by `media.digital`, and stores via S3. On success we populate
   * digitalSettings.downloadUrl (so mobile keeps working) and capture
   * file metadata in digitalFileMeta for nicer UI display.
   */
  const handleDigitalUpload = async (e) => {
    setDigitalError('');
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    if (!hasDigitalAccess) {
      setDigitalError('Digital file uploads require a paid plan. You can still paste an external link below.');
      return;
    }
    if (digitalLimit && file.size > digitalLimit) {
      setDigitalError(
        `Your plan allows files up to ${formatBytes(digitalLimit)}. This file is ${formatBytes(file.size)}.`
      );
      return;
    }

    setDigitalUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post('/upload/digital-asset', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.url) {
        const meta = {
          url: res.data.url,
          originalName: res.data.originalName || file.name,
          size: res.data.size || file.size,
          mimeType: res.data.mimeType || file.type,
        };
        setDigitalFileMeta(meta);
        setDigitalSettings({
          ...digitalSettings,
          downloadUrl: res.data.url,
        });
      } else {
        setDigitalError('Upload completed but no URL was returned.');
      }
    } catch (err) {
      const data = err?.response?.data || {};
      setDigitalError(data.error || 'Failed to upload file. Please try again.');
    } finally {
      setDigitalUploading(false);
    }
  };

  const handleDigitalRemove = () => {
    setDigitalFileMeta(null);
    setDigitalError('');
    setDigitalSettings({
      ...digitalSettings,
      downloadUrl: '',
    });
  };

  const uploadImages = async () => {
    const formData = new FormData();

    if (primaryImageFile) {
      formData.append('primaryImage', primaryImageFile);
    }

    galleryImages.forEach((file, index) => {
      formData.append(`galleryImage${index}`, file);
    });

    try {
      setUploading(true);
      const response = await axios.post('/upload/images', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data.urls;
    } catch (error) {
      console.error('Error uploading images:', error);
      throw error;
    } finally {
      setUploading(false);
    }
  };

  // Appointment Settings Helpers
  const handleDayToggle = (day) => {
    const newDays = appointmentSettings.availableDays.includes(day)
      ? appointmentSettings.availableDays.filter(d => d !== day)
      : [...appointmentSettings.availableDays, day];

    setAppointmentSettings({
      ...appointmentSettings,
      availableDays: newDays
    });
  };

  const handleAddTimeSlot = (day, start, end) => {
    const newSlots = [...appointmentSettings.availableTimeSlots, { day, start, end }];
    setAppointmentSettings({
      ...appointmentSettings,
      availableTimeSlots: newSlots
    });
  };

  const handleRemoveTimeSlot = (index) => {
    const newSlots = appointmentSettings.availableTimeSlots.filter((_, i) => i !== index);
    setAppointmentSettings({
      ...appointmentSettings,
      availableTimeSlots: newSlots
    });
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: FileText },
    { id: 'pricing', label: 'Pricing', icon: DollarSign },
    { id: 'inventory', label: 'Inventory', icon: Boxes },
    { id: 'media', label: 'Media', icon: Image },
    ...(productType === 'digital' ? [{ id: 'digital', label: 'Digital Delivery', icon: Download }] : []),
    ...(productType === 'appointment' ? [{ id: 'appointments', label: 'Appointments', icon: Calendar }] : []),
    { id: 'variations', label: 'Variations', icon: Layers },
  ];

  return (
    <div className={`product-form-container ${isSections ? 'pf-layout-sections' : 'pf-layout-tabs'}`}>
      {/* Legacy tab bar — only shown in tabs layout. */}
      {!isSections && (
        <div className="form-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              type="button"
              className={`tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="tab-icon">{<tab.icon size={18} />}</span>
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </div>
      )}

      <form id={formId} onSubmit={handleSubmit} className="product-form">
        {/* Basic Info Tab */}
        {sectionVisible('basic') && (
          <div className="tab-content" id="pfs-section-basic" data-pfs-section="basic">
            <div className="form-row">
              <div className="form-group">
                <label>Product Name *</label>
                <Input
                  value={name}
                  onChange={handleNameChange}
                  placeholder="Enter product name"
                  required
                />
              </div>

              <div className="form-group">
                <label>Category *</label>
                <select
                  className="form-select"
                  value={categoryId || category}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Check if it's a category ID or legacy string value
                    const selectedCat = dynamicCategories.find(c => c.id === value) ||
                      dynamicCategories.flatMap(c => c.children || []).find(c => c.id === value);
                    if (selectedCat) {
                      setCategoryId(value);
                      setCategory(selectedCat.name);
                    } else {
                      // Legacy value
                      setCategoryId('');
                      setCategory(value);
                    }
                  }}
                  required
                >
                  <option value="">Select category</option>
                  {dynamicCategories.length > 0 ? (
                    // Dynamic categories from API
                    dynamicCategories.map(cat => (
                      <optgroup key={cat.id} label={cat.name}>
                        <option value={cat.id}>{cat.name}</option>
                        {cat.children?.map(sub => (
                          <option key={sub.id} value={sub.id}>
                            &nbsp;&nbsp;{sub.name}
                          </option>
                        ))}
                      </optgroup>
                    ))
                  ) : (
                    // Fallback to legacy hardcoded options
                    <>
                      <option value="clothing">Clothing</option>
                      <option value="electronics">Electronics</option>
                      <option value="home">Home & Garden</option>
                      <option value="beauty">Beauty & Health</option>
                      <option value="sports">Sports & Outdoors</option>
                      <option value="books">Books & Media</option>
                      <option value="toys">Toys & Games</option>
                      <option value="automotive">Automotive</option>
                      <option value="other">Other</option>
                    </>
                  )}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>URL Slug</label>
              <Input
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="product-url-slug"
              />
              <span className="form-hint">Auto-generated from name if empty</span>
            </div>

            <div className="form-group">
              <div className="form-label-row">
                <label>Description</label>
                <div className="pf-label-actions">
                  {description && (
                    <button
                      type="button"
                      className="pf-toggle-pill"
                      onClick={() => setDescPreview((v) => !v)}
                      title={descPreview ? 'Edit HTML source' : 'Preview rendered output'}
                      aria-pressed={descPreview}
                    >
                      {descPreview ? <CodeIcon size={13} /> : <Eye size={13} />}
                      <span>{descPreview ? 'Edit' : 'Preview'}</span>
                    </button>
                  )}
                  <button
                    type="button"
                    className="pf-ai-pill"
                    onClick={generateContent}
                    disabled={generating || generatingImage || !name.trim()}
                    title={!name.trim() ? 'Enter a product name first' : 'Generate description with AI'}
                  >
                    <Sparkles size={13} aria-hidden="true" />
                    <span>{generating ? 'Generating…' : 'Generate description'}</span>
                  </button>
                </div>
              </div>
              {descPreview ? (
                <div
                  className="pf-description-preview"
                  // The AI route sanitises HTML server-side before returning;
                  // sanitizeDescriptionHtml is belt-and-braces for any HTML
                  // the merchant pasted manually.
                  dangerouslySetInnerHTML={{ __html: sanitizeDescriptionHtml(description) }}
                />
              ) : (
                <textarea
                  className="form-textarea"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Product description — HTML supported (paragraphs, headings, lists, tables)"
                  rows={7}
                />
              )}
              <span className="form-hint">
                HTML supported. Use <strong>Generate description</strong> to produce a structured intro + key features + spec table.
              </span>
              {aiError && <span className="form-hint pf-ai-error">{aiError}</span>}
            </div>

            <div className="form-group">
              <label>Tags</label>
              <Input
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="tag1, tag2, tag3"
              />
              <span className="form-hint">Separate with commas</span>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Product Type</label>
                <select
                  className="form-select"
                  value={productType}
                  onChange={(e) => setProductType(e.target.value)}
                >
                  <option value="physical">Physical Product</option>
                  <option value="digital">Digital Product</option>
                  <option value="service">Service</option>
                  <option value="appointment">Appointment/Consultation</option>
                </select>
                <small className="field-hint">
                  {productType === 'digital' && 'Configure digital delivery settings in the Digital Delivery tab'}
                  {productType === 'appointment' && 'Configure appointment settings in the Appointments tab'}
                </small>
              </div>

              <div className="form-group">
                <label>Status</label>
                <select
                  className="form-select"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="DRAFT">Draft</option>
                  <option value="ACTIVE">Published</option>
                  <option value="ARCHIVED">Archived</option>
                </select>
              </div>
            </div>

            {/* Phase 4.A.6 polish — storefront badges as a single
                pill-strip row. Four toggles in one place beats four
                separate checkbox rows (~70 lines of JSX → ~30) and
                gives the merchant a clearer sense of "what badges does
                this product wear on the storefront." */}
            <div className="pf-badges">
              <label className="pf-badges-label">Storefront badges</label>
              <div className="pf-badges-row">
                {[
                  { id: 'featured', label: 'Featured', on: featured, set: setFeatured, dot: '#0d7463' },
                  { id: 'bestSeller', label: 'Best seller', on: bestSeller, set: setBestSeller, dot: '#f59e0b' },
                  { id: 'trending', label: 'Trending', on: trending, set: setTrending, dot: '#10b981' },
                  { id: 'newArrival', label: 'New arrival', on: newArrival, set: setNewArrival, dot: '#6366f1' },
                ].map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    className={`pf-badge ${b.on ? 'is-on' : ''}`}
                    onClick={() => b.set(!b.on)}
                    aria-pressed={b.on}
                  >
                    <span className="pf-badge-dot" style={{ background: b.dot }} />
                    <span>{b.label}</span>
                  </button>
                ))}
              </div>
              <span className="pf-badges-hint">
                Tap to toggle which homepage sections this product appears in.
              </span>
            </div>

            {/* AI Enhancement callout removed — Generate description is now
                inline above the Description field, and Generate image is
                above the Primary Image upload zone in the Media section. */}
          </div>
        )}

        {/* Pricing Tab */}
        {sectionVisible('pricing') && (
          <div className="tab-content" id="pfs-section-pricing" data-pfs-section="pricing">
            {/* Free Appointment Notice */}
            {productType === 'appointment' && isFreeAppointment && (
              <div style={{
                padding: '16px',
                backgroundColor: '#ecfdf5',
                border: '2px solid #10b981',
                borderRadius: '8px',
                marginBottom: '24px'
              }}>
                <p style={{ margin: '0', fontSize: '14px', color: '#047857', fontWeight: '600' }}>
                  ✓ This is a free appointment - no payment required
                </p>
                <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#059669' }}>
                  Customers can book without payment. To enable paid bookings, uncheck "This is a free appointment" in the Appointments tab.
                </p>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>Price {productType === 'appointment' && !isFreeAppointment ? '*' : ''}</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="0.00"
                  required={productType !== 'appointment' || !isFreeAppointment}
                  disabled={productType === 'appointment' && isFreeAppointment}
                  style={productType === 'appointment' && isFreeAppointment ? {
                    opacity: 0.5,
                    cursor: 'not-allowed',
                    backgroundColor: '#f3f4f6'
                  } : {}}
                />
                {productType === 'appointment' && isFreeAppointment && (
                  <small className="field-hint" style={{ color: '#10b981' }}>
                    ✓ Price is set to ₦0 for free appointments
                  </small>
                )}
                {/* Phase 4.A.6 polish — markup-driven suggestion chip.
                    Appears whenever the current Price doesn't equal
                    cost × (1 + markup%). One click applies it. */}
                {markupSuggestedPrice != null && !priceMatchesMarkup && (
                  <button
                    type="button"
                    className="pf-markup-chip"
                    onClick={() => setPrice(String(markupSuggestedPrice))}
                  >
                    Apply ₦{markupSuggestedPrice.toLocaleString()} ({effectiveMarkupPercent}% markup)
                  </button>
                )}
              </div>

              <div className="form-group">
                <label>Compare At Price</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={compareAtPrice}
                  onChange={(e) => setCompareAtPrice(e.target.value)}
                  placeholder="0.00"
                />
                <span className="form-hint">Original price for discounts</span>
              </div>
            </div>

            {/* Cost & Margin — drives Finance KPIs / per-product margin reports.
                Live margin preview helps merchants sanity-check pricing decisions. */}
            <div className="form-row">
              <div className="form-group">
                <label>Unit Cost</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={costPrice}
                  onChange={(e) => {
                    const newCost = e.target.value;
                    setCostPrice(newCost);
                    // Phase 4.A.6 polish — auto-fill Price from markup when
                    // (a) creating a new product, (b) markup is known, and
                    // (c) Price is empty or matches the OLD markup-suggested
                    // value (so we don't clobber a hand-typed override).
                    if (!isEditingExisting && effectiveMarkupPercent != null) {
                      const c = parseFloat(newCost);
                      if (Number.isFinite(c) && c > 0) {
                        const oldSuggested = (() => {
                          const oldC = parseFloat(costPrice);
                          if (!Number.isFinite(oldC) || oldC <= 0) return null;
                          return Number((oldC * (1 + effectiveMarkupPercent / 100)).toFixed(2));
                        })();
                        const currentPriceNum = parseFloat(price);
                        const priceIsBlank = !price || price === '' || currentPriceNum === 0;
                        const priceIsOldSuggested = oldSuggested != null && currentPriceNum === oldSuggested;
                        if (priceIsBlank || priceIsOldSuggested) {
                          const newSuggested = Number((c * (1 + effectiveMarkupPercent / 100)).toFixed(2));
                          setPrice(String(newSuggested));
                        }
                      }
                    }
                  }}
                  placeholder="0.00"
                />
                <span className="form-hint">What you paid for this. Drives your profit calculations.</span>
                {/* Phase 4.A.6 polish — silent-failure nudge. If markup is
                    set but Unit Cost is blank, tell the merchant their
                    markup can't compute anything yet. */}
                {effectiveMarkupPercent != null &&
                 (!costPrice || costPrice === '' || parseFloat(costPrice) <= 0) && (
                  <span className="form-hint pf-markup-nudge">
                    ⓘ Add a Unit Cost so your {effectiveMarkupPercent}% markup can compute the sell price.
                  </span>
                )}
              </div>

              <div className="form-group">
                <label>Margin</label>
                <div
                  className="form-static-readout"
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    background: '#f9fafb',
                    color: '#0f172a',
                    fontWeight: 600,
                    fontSize: 14,
                    minHeight: 40,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {(() => {
                    const p = parseFloat(price);
                    const c = parseFloat(costPrice);
                    if (!Number.isFinite(p) || p <= 0 || !Number.isFinite(c)) {
                      return <span style={{ color: '#9ca3af', fontWeight: 400 }}>—</span>;
                    }
                    const pct = ((p - c) / p) * 100;
                    const tone = pct >= 30 ? '#047857' : pct >= 10 ? '#a16207' : '#b91c1c';
                    return <span style={{ color: tone }}>{pct.toFixed(1)}%</span>;
                  })()}
                </div>
                <span className="form-hint">Auto-calculated from price and unit cost.</span>
              </div>
            </div>

            {/* Phase 4.A.6 polish — markup %. Inherits from category
                or store default by default; merchant can override.
                The MarkupField component owns the inherited-vs-override
                display states. */}
            <div className="form-row">
              <div className="form-group">
                <MarkupField
                  inherited={inheritedMarkup.value}
                  inheritedFrom={inheritedMarkup.from}
                  value={markupPercent}
                  onChange={setMarkupPercent}
                  onConfigureClick={() => window.open('/settings?tab=general#pricing-defaults', '_blank')}
                />
              </div>
              <div className="form-group">
                {/* spacer so the field doesn't span the full row */}
              </div>
            </div>

            {/* Phase 4.A.6 polish — Unit-Based Selling collapsed by
                default. Most products don't sell by weight/length/etc,
                so hiding it behind a disclosure cuts ~120 lines from
                the visible Pricing tab without losing the feature.
                <details>/<summary> is the native HTML disclosure
                widget — accessible by default, no extra ARIA needed. */}
            <details
              className="pf-disclosure"
              open={Boolean(sellByUnit) || Boolean(minOrderQuantity)}
            >
              <summary className="pf-disclosure-summary">
                <span>Sell by unit (kg, yard, liter…)</span>
                <span className="pf-disclosure-hint">
                  {sellByUnit ? `Enabled — ${unitOfMeasure}` : 'Optional — for weight or volume-based pricing'}
                </span>
              </summary>
              <p style={{ fontSize: '13px', color: '#6b7280', margin: '12px 0 16px' }}>
                Sell products by weight, length, volume, or area (e.g., per kg, yard, liter)
              </p>

              {/* Enable Sell by Unit Toggle */}
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={sellByUnit}
                    onChange={(e) => {
                      setSellByUnit(e.target.checked);
                      if (e.target.checked && unitOfMeasure === 'PIECE') {
                        setUnitOfMeasure('KG');
                        setMinOrderQuantity('0.1');
                        setStepIncrement('0.1');
                      } else if (!e.target.checked) {
                        setUnitOfMeasure('PIECE');
                        setMinOrderQuantity('');
                        setStepIncrement('');
                      }
                    }}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span style={{ fontWeight: '500' }}>Enable unit-based selling</span>
                </label>
                <small className="field-hint">Enable to sell by kg, yard, meter, etc.</small>
              </div>

              {/* Unit Options - Only show when sellByUnit is enabled */}
              {sellByUnit && (
                <div style={{ backgroundColor: '#f9fafb', borderRadius: '12px', padding: '20px', border: '1px solid #e5e7eb' }}>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Unit of Measure *</label>
                      <select
                        value={unitOfMeasure}
                        onChange={(e) => {
                          const unit = e.target.value;
                          setUnitOfMeasure(unit);
                          const unitInfo = UNIT_OPTIONS.find(u => u.value === unit);
                          if (!unitInfo?.allowDecimal) {
                            setMinOrderQuantity('1');
                            setStepIncrement('1');
                          } else if (!minOrderQuantity || minOrderQuantity === '1') {
                            setMinOrderQuantity('0.1');
                            setStepIncrement('0.1');
                          }
                        }}
                        className="form-select"
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          borderRadius: '8px',
                          border: '1px solid #d1d5db',
                          fontSize: '14px',
                          backgroundColor: '#fff'
                        }}
                      >
                        {UNIT_OPTIONS.filter(u => u.value !== 'PIECE').map(unit => (
                          <option key={unit.value} value={unit.value}>
                            {unit.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-row" style={{ marginTop: '16px' }}>
                    <div className="form-group">
                      <label>Minimum Order Quantity</label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={minOrderQuantity}
                        onChange={(e) => setMinOrderQuantity(e.target.value)}
                        placeholder="0.1"
                      />
                      <small className="field-hint">Smallest amount customers can order</small>
                    </div>

                    <div className="form-group">
                      <label>Step Increment</label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={stepIncrement}
                        onChange={(e) => setStepIncrement(e.target.value)}
                        placeholder="0.1"
                      />
                      <small className="field-hint">Quantity increase/decrease step</small>
                    </div>
                  </div>

                  {/* Price Display Preview */}
                  {price && (
                    <div style={{
                      marginTop: '16px',
                      padding: '12px 16px',
                      backgroundColor: '#fff',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      textAlign: 'center'
                    }}>
                      <span style={{ fontSize: '13px', color: '#6b7280' }}>Price will be displayed as: </span>
                      <span style={{ fontSize: '16px', fontWeight: '700', color: '#0d7463' }}>
                        {new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(parseFloat(price) || 0)}/
                        {UNIT_OPTIONS.find(u => u.value === unitOfMeasure)?.label.match(/\(([^)]+)\)/)?.[1] || unitOfMeasure.toLowerCase()}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </details>
          </div>
        )}

        {/* Inventory Tab — Phase 4.A.6. Quantity, SKU, expiry, and
            low-stock alert live here so the Basics/Pricing tabs stay
            focused. Edit mode shows the read-only aggregate + Receive
            new stock CTA (which opens the batch-creation sidebar).
            Create mode shows an editable initial-quantity input that
            seeds the OPENING_STOCK batch on save. */}
        {sectionVisible('inventory') && (
          <div className="tab-content" id="pfs-section-inventory" data-pfs-section="inventory">
            <div className="form-row">
              <div className="form-group">
                <label>Quantity {isEditingExisting ? '' : '*'}</label>
                {isEditingExisting ? (
                  <>
                    <div className="fld-quantity-readonly">
                      <span className="fld-quantity-value">
                        {displayQuantity}
                        <span className="fld-quantity-unit">in stock</span>
                      </span>
                      <button
                        type="button"
                        className="fld-receive-stock-cta"
                        onClick={() => setReceiveStockOpen(true)}
                      >
                        <PackagePlus size={14} />
                        Receive new stock
                      </button>
                    </div>
                    <span className="fld-quantity-hint">
                      Stock is batch-tracked. Use Receive new stock to add units with an expiry, cost, and supplier reference.
                    </span>
                  </>
                ) : (
                  <Input
                    type="number"
                    min="0"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    placeholder="0"
                    required
                  />
                )}
              </div>

              <div className="form-group">
                <label>SKU</label>
                <Input
                  value={sku}
                  onChange={(e) => {
                    setSku(e.target.value);
                    setSkuManuallyEdited(true);
                  }}
                  placeholder="Auto-generated from product name"
                />
                {!isEditingExisting && !skuManuallyEdited && sku && (
                  <span className="form-hint">
                    Auto-generated from the product name. Edit to set your own.
                  </span>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Expiry date</label>
                <Input
                  type="date"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                />
                <span className="form-hint">
                  Optional. Leave blank for non-perishables. Drives the Expiring Stock tab and the past-expiry sales gate.
                </span>
              </div>

              <div className="form-group">
                <label>Low stock alert</label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={lowStockThreshold}
                  onChange={(e) => setLowStockThreshold(e.target.value)}
                  placeholder="e.g. 5"
                />
                <span className="form-hint">Optional. Get alerted when stock falls below this.</span>
              </div>
            </div>

            <div className="pf-supplier-card">
              <div className="pf-supplier-head">
                <div className="pf-supplier-icon">
                  <Truck size={18} aria-hidden="true" />
                </div>
                <div>
                  <div className="pf-supplier-kicker">Premium inventory</div>
                  <h4>Supplier reorder</h4>
                  <p>Link this product to a supplier and email a reorder request when stock runs low.</p>
                </div>
              </div>

              {!isEditingExisting ? (
                <div className="pf-supplier-empty">
                  Save this product first, then add supplier reorder details.
                </div>
              ) : supplierAccessLoading || supplierLoading ? (
                <div className="pf-supplier-empty">Loading supplier setup...</div>
              ) : !hasSupplierReorderAccess ? (
                <div className="pf-supplier-locked">
                  <Lock size={16} aria-hidden="true" />
                  <span>Supplier reorder notifications are available on Premium.</span>
                </div>
              ) : (
                <>
                  <div className="pf-supplier-status">
                    <span className={supplierIsLowStock ? 'is-low' : ''}>
                      {supplierCurrentStock} in stock
                    </span>
                    <span>Alert threshold: {Number.isFinite(supplierThreshold) ? supplierThreshold : 5}</span>
                    <span>{supplierIsLowStock ? 'Ready to reorder' : 'Above threshold'}</span>
                  </div>

                  {supplierContext?.suppliers?.length > 0 && (
                    <div className="form-group pf-supplier-select">
                      <label>Use saved supplier</label>
                      <select
                        value={supplierForm.supplierId}
                        onChange={(e) => handleSupplierSelect(e.target.value)}
                      >
                        <option value="">Add a new supplier</option>
                        {supplierContext.suppliers.map((supplier) => (
                          <option key={supplier.id} value={supplier.id}>
                            {supplier.name}{supplier.email ? ` - ${supplier.email}` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="pf-supplier-grid">
                    <div className="form-group">
                      <label>Supplier name</label>
                      <Input
                        value={supplierForm.name}
                        onChange={(e) => handleSupplierFieldChange('name', e.target.value)}
                        placeholder="e.g. Mainland Drinks Depot"
                      />
                    </div>
                    <div className="form-group">
                      <label>Contact person</label>
                      <Input
                        value={supplierForm.contactName}
                        onChange={(e) => handleSupplierFieldChange('contactName', e.target.value)}
                        placeholder="Optional"
                      />
                    </div>
                    <div className="form-group">
                      <label>Email</label>
                      <Input
                        type="email"
                        value={supplierForm.email}
                        onChange={(e) => handleSupplierFieldChange('email', e.target.value)}
                        placeholder="supplier@example.com"
                      />
                    </div>
                    <div className="form-group">
                      <label>WhatsApp number</label>
                      <Input
                        value={supplierForm.whatsappNumber}
                        onChange={(e) => handleSupplierFieldChange('whatsappNumber', e.target.value)}
                        placeholder="+234..."
                      />
                    </div>
                    <div className="form-group">
                      <label>Reorder quantity</label>
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        value={supplierForm.reorderQuantity}
                        onChange={(e) => handleSupplierFieldChange('reorderQuantity', e.target.value)}
                        placeholder="e.g. 20"
                      />
                    </div>
                  </div>

                  <div className="pf-supplier-actions">
                    <button
                      type="button"
                      className="pf-supplier-save"
                      onClick={saveSupplierSetup}
                      disabled={supplierSaving}
                    >
                      {supplierSaving ? 'Saving...' : 'Save supplier'}
                    </button>
                    <button
                      type="button"
                      className="pf-supplier-send"
                      onClick={notifySupplierNow}
                      disabled={supplierNotifying || !supplierForm.supplierId || !supplierIsLowStock}
                      title={!supplierIsLowStock ? 'Product has not reached its low-stock threshold' : 'Email supplier now'}
                    >
                      {supplierNotifying ? (
                        'Sending...'
                      ) : (
                        <>
                          <Send size={14} aria-hidden="true" />
                          Email supplier now
                        </>
                      )}
                    </button>
                  </div>

                  <div className="pf-supplier-note">
                    <Mail size={14} aria-hidden="true" />
                    WhatsApp supplier alerts will be added after template approval. This first version sends explicit merchant-triggered email only.
                  </div>

                  {supplierContext?.notifications?.length > 0 && (
                    <div className="pf-supplier-history">
                      <span>Recent sends</span>
                      {supplierContext.notifications.slice(0, 3).map((notification) => (
                        <div key={notification.id} className="pf-supplier-history-row">
                          <strong>{notification.status}</strong>
                          <span>{notification.recipientEmail}</span>
                          <time>
                            {new Date(notification.sentAt || notification.createdAt).toLocaleDateString()}
                          </time>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Media Tab */}
        {sectionVisible('media') && (
          <div className="tab-content" id="pfs-section-media" data-pfs-section="media">
            <div className="media-grid">
              <div className="media-field pf-media-with-ai">
                <label>Primary Image</label>
                <div
                  className={`media-upload ${imageUrl ? 'has-image' : ''}`}
                  onClick={() => document.getElementById('primary-image-upload').click()}
                >
                  {/* Floating AI chip — sits in the top-right corner of the
                      upload zone so it doesn't push the box down or break
                      column alignment with Gallery Images. */}
                  <button
                    type="button"
                    className="pf-ai-pill pf-ai-pill-floating"
                    onClick={(e) => {
                      e.stopPropagation();
                      generateImage();
                    }}
                    disabled={generating || generatingImage || !name.trim()}
                    title={!name.trim() ? 'Enter a product name first' : 'Generate image with AI'}
                  >
                    <Sparkles size={13} aria-hidden="true" />
                    <span>{generatingImage ? 'Generating…' : 'Generate'}</span>
                  </button>
                  <input
                    id="primary-image-upload"
                    type="file"
                    accept="image/*"
                    onChange={handlePrimaryImageUpload}
                    style={{ display: 'none' }}
                  />
                  {imageUrl ? (
                    <div className="media-preview">
                      <img src={getImageUrl(imageUrl)} alt="Preview" />
                      <div className="media-overlay">
                        <span>✓</span>
                        <p>Click to change</p>
                      </div>
                    </div>
                  ) : (
                    <div className="media-placeholder">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                        stroke="#9ca3af" strokeWidth="1.5"
                        strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                      </svg>
                      <p>Click to upload</p>
                      <button
                        type="button"
                        className="pf-media-library-link"
                        onClick={(e) => {
                          e.stopPropagation();
                          setMediaType('image');
                          setShowMediaLibrary(true);
                        }}
                      >
                        or choose from library
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="media-field">
                <label>Gallery Images</label>
                <div
                  className={`media-upload ${galleryImages.length > 0 || images ? 'has-image' : ''}`}
                  onClick={() => document.getElementById('gallery-images-upload').click()}
                >
                  <input
                    id="gallery-images-upload"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleGalleryImageUpload}
                    style={{ display: 'none' }}
                  />
                  {(galleryImages.length > 0 || images) ? (
                    <div className="media-placeholder">
                      <span>✓</span>
                      <p>
                        {galleryImages.length > 0 ?
                          `${galleryImages.length} image${galleryImages.length > 1 ? 's' : ''}` :
                          `${images.split(',').filter(img => img.trim()).length} image${images.split(',').filter(img => img.trim()).length > 1 ? 's' : ''}`
                        }
                      </p>
                      <button
                        type="button"
                        className="pf-media-library-link"
                        onClick={(e) => {
                          e.stopPropagation();
                          setMediaType('gallery');
                          setShowMediaLibrary(true);
                        }}
                      >
                        manage gallery
                      </button>
                    </div>
                  ) : (
                    <div className="media-placeholder">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                        <circle cx="8.5" cy="8.5" r="1.5"/>
                        <polyline points="21 15 16 10 5 21"/>
                      </svg>
                      <p>Click to upload</p>
                      <button
                        type="button"
                        className="pf-media-library-link"
                        onClick={(e) => {
                          e.stopPropagation();
                          setMediaType('gallery');
                          setShowMediaLibrary(true);
                        }}
                      >
                        or choose from library
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Phase 4.A.6 polish — Video as the 3rd tile in the
                  media grid (was a full-width subsection below). Same
                  size shell as Primary + Gallery so the row reads as
                  a uniform "media row." Feature gate preserved: FREE
                  tier sees the locked tile with the Upgrade CTA;
                  paid tiers see the upload zone. */}
              <div className="media-field pf-media-video">
                <label>
                  Product video
                  {hasVideoAccess && videoLimit > 0 && (
                    <span className="pf-media-sublabel"> · max {formatBytes(videoLimit)}</span>
                  )}
                </label>
                {videoAccessLoading ? (
                  <div className="media-upload pf-video-loading-shell">Checking plan…</div>
                ) : !hasVideoAccess ? (
                  <div className="media-upload pf-video-locked-shell">
                    <div className="pf-video-locked-icon" aria-hidden="true">✦</div>
                    <div className="pf-video-locked-title">PRO and above</div>
                    <div className="pf-video-locked-text">
                      Up to {formatBytes(videoLimitForTier('PRO'))} on PRO, {formatBytes(videoLimitForTier('PREMIUM'))} on PREMIUM.
                    </div>
                    <a href="/settings?tab=billing" className="pf-video-locked-cta">Upgrade</a>
                  </div>
                ) : videoUrl ? (
                  <div className="media-upload has-video">
                    <video
                      className="pf-video-player-tile"
                      src={videoUrl}
                      controls
                      preload="metadata"
                    />
                    <input
                      id="product-video-upload"
                      type="file"
                      accept={VIDEO_ACCEPT}
                      onChange={handleVideoUpload}
                      style={{ display: 'none' }}
                    />
                  </div>
                ) : (
                  <div
                    className="media-upload"
                    onClick={() => document.getElementById('product-video-upload').click()}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="media-placeholder">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                        stroke="#9ca3af" strokeWidth="1.5"
                        strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="23 7 16 12 23 17 23 7" />
                        <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                      </svg>
                      <p>{videoUploading ? 'Uploading…' : 'Upload video'}</p>
                    </div>
                    <input
                      id="product-video-upload"
                      type="file"
                      accept={VIDEO_ACCEPT}
                      onChange={handleVideoUpload}
                      style={{ display: 'none' }}
                    />
                  </div>
                )}
                {/* Phase 4.A.6 polish v3 — when populated, action chips
                    sit absolutely-positioned over the bottom of the video
                    tile so they don't push it taller than its siblings.
                    See .pf-media-tile-actions in ProductForm.css. */}
                {hasVideoAccess && videoUrl && (
                  <div className="pf-media-tile-actions pf-media-tile-actions--overlay">
                    <button
                      type="button"
                      className="pf-media-library-link"
                      onClick={() => document.getElementById('product-video-upload').click()}
                      disabled={videoUploading}
                    >
                      {videoUploading ? 'Uploading…' : 'Replace'}
                    </button>
                    <button
                      type="button"
                      className="pf-media-library-link pf-media-library-link--danger"
                      onClick={handleVideoRemove}
                      disabled={videoUploading}
                    >
                      Remove
                    </button>
                  </div>
                )}
                {videoError && <div className="pf-video-error">{videoError}</div>}
              </div>
            </div>
          </div>
        )}

        {/* Variations Tab - Variant Matrix */}
        {sectionVisible('variations') && (
          <div className="tab-content" id="pfs-section-variations" data-pfs-section="variations">
            <div className="variations-header" style={{ marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Product Variations</h3>
              <p className="field-hint" style={{ margin: '0.5rem 0 0 0' }}>
                Create variations like Size, Color, or Material. Each combination becomes a unique variant with its own price, stock, and SKU.
              </p>
            </div>

            {isSections ? (
              /* In the new full-page layout the matrix lives inside a drawer
                 — section card shows a compact summary + a single CTA. */
              <div className="pf-variations-summary">
                {variantMatrix?.attributes?.length > 0 ? (
                  <div className="pf-variations-summary-card">
                    <div className="pf-variations-summary-body">
                      <div className="pf-variations-summary-title">
                        {variantMatrix.attributes.length} attribute{variantMatrix.attributes.length === 1 ? '' : 's'}
                        {' · '}
                        {variantMatrix.variants?.length || 0} variant{(variantMatrix.variants?.length || 0) === 1 ? '' : 's'}
                      </div>
                      <div className="pf-variations-summary-meta">
                        {variantMatrix.attributes
                          .map((a) => `${a.name} (${(a.values || []).length})`)
                          .join(' · ')}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="pf-variations-edit-btn"
                      onClick={() => setVariantDrawerOpen(true)}
                    >
                      Edit
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="pf-variations-add-btn"
                    onClick={() => setVariantDrawerOpen(true)}
                  >
                    <span className="pf-variations-add-icon" aria-hidden="true">+</span>
                    <span className="pf-variations-add-body">
                      <span className="pf-variations-add-title">Add variations</span>
                      <span className="pf-variations-add-sub">
                        Sizes, colors, materials — each combination becomes its own variant.
                      </span>
                    </span>
                  </button>
                )}
              </div>
            ) : (
              /* Legacy tabs layout — keep the inline matrix as before. */
              <VariantMatrix
                value={variantMatrix}
                onChange={handleVariantMatrixChange}
                basePrice={parseFloat(price) || 0}
                baseSku={sku || ''}
                attributeLimit={attributeLimit}
                disabled={loading}
              />
            )}
          </div>
        )}

        {/* Digital Delivery Tab */}
        {sectionVisible('digital') && productType === 'digital' && (
          <div className="tab-content digital-settings" id="pfs-section-digital" data-pfs-section="digital">
            <h3 className="section-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Digital Delivery Settings
            </h3>

            {/* Info Banner */}
            <div style={{ padding: '16px', backgroundColor: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe', marginBottom: '24px' }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#1e40af' }}>
                <strong>How it works:</strong> After payment, customers automatically receive an email with a secure download link.
                You can set download limits and expiration to protect your content.
              </p>
            </div>

            {/* File / link delivery — upload to our S3 (tier-gated) or
                paste any external URL (always available). The two routes
                coexist; whichever is provided becomes the deliverable. */}
            <div className="pf-digital-section">
              <div className="pf-digital-head">
                <div>
                  <div className="pf-digital-title">Digital file</div>
                  <div className="pf-digital-sub">
                    {hasDigitalAccess && digitalLimit > 0
                      ? `Up to ${formatBytes(digitalLimit)} · PDF, EPUB, MOBI, ZIP, or DOCX`
                      : 'External URLs work on every plan. File uploads require PRO or above.'}
                  </div>
                </div>
              </div>

              {digitalAccessLoading ? (
                <div className="pf-digital-loading">Checking plan…</div>
              ) : !hasDigitalAccess ? (
                <div className="pf-digital-locked">
                  <div className="pf-digital-locked-icon" aria-hidden="true">✦</div>
                  <div className="pf-digital-locked-body">
                    <div className="pf-digital-locked-title">File uploads available on PRO and above</div>
                    <div className="pf-digital-locked-text">
                      Host PDFs, eBooks, ZIPs, or documents directly on Revstar.
                      {' '}{formatBytes(digitalLimitForTier('PRO'))} (PRO), {formatBytes(digitalLimitForTier('PREMIUM'))} (PREMIUM).
                      External URLs are available on every plan — paste one below.
                    </div>
                  </div>
                  <a href="/settings?tab=billing" className="pf-digital-locked-cta">Upgrade</a>
                </div>
              ) : digitalFileMeta?.url ? (
                <div className="pf-digital-filled">
                  <div className="pf-digital-file-icon" aria-hidden="true">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                  </div>
                  <div className="pf-digital-file-info">
                    <div className="pf-digital-file-name" title={digitalFileMeta.originalName}>
                      {digitalFileMeta.originalName || 'Uploaded file'}
                    </div>
                    {(digitalFileMeta.size > 0 || digitalFileMeta.mimeType) && (
                      <div className="pf-digital-file-meta">
                        {digitalFileMeta.size > 0 ? formatBytes(digitalFileMeta.size) : ''}
                        {digitalFileMeta.size > 0 && digitalFileMeta.mimeType ? ' · ' : ''}
                        {digitalFileMeta.mimeType ? (digitalFileMeta.mimeType.split('/')[1]?.toUpperCase() || digitalFileMeta.mimeType) : ''}
                      </div>
                    )}
                  </div>
                  <div className="pf-digital-actions">
                    <label className="pf-digital-replace-btn">
                      {digitalUploading ? 'Uploading…' : 'Replace'}
                      <input
                        type="file"
                        accept={DIGITAL_ACCEPT}
                        onChange={handleDigitalUpload}
                        disabled={digitalUploading}
                        hidden
                      />
                    </label>
                    <button
                      type="button"
                      className="pf-digital-remove-btn"
                      onClick={handleDigitalRemove}
                      disabled={digitalUploading}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <label className="pf-digital-upload-btn">
                  <div className="pf-digital-upload-icon" aria-hidden="true">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                  </div>
                  <div className="pf-digital-upload-body">
                    <span className="pf-digital-upload-title">
                      {digitalUploading ? 'Uploading…' : 'Upload digital file'}
                    </span>
                    <span className="pf-digital-upload-hint">
                      Click to select a file · max {formatBytes(digitalLimit)}
                    </span>
                  </div>
                  <input
                    type="file"
                    accept={DIGITAL_ACCEPT}
                    onChange={handleDigitalUpload}
                    disabled={digitalUploading}
                    hidden
                  />
                </label>
              )}

              {digitalError && <div className="pf-digital-error">{digitalError}</div>}

              {/* External URL fallback — works on every tier. Lives below
                  the upload zone so the recommended path (upload) reads
                  first, but pasting a URL is always available. */}
              <div className="pf-digital-divider"><span>or use an external URL</span></div>

              <div className="form-group" style={{ marginTop: 0 }}>
                <label>External download URL</label>
                <Input
                  type="url"
                  value={digitalSettings.externalUrl || ''}
                  onChange={(e) => setDigitalSettings({
                    ...digitalSettings,
                    externalUrl: e.target.value
                  })}
                  placeholder="https://drive.google.com/file/... or https://dropbox.com/..."
                />
                <small className="field-hint">
                  Paste a link from Google Drive, Dropbox, OneDrive, or any direct download URL. Available on every plan.
                </small>
              </div>
            </div>

            {/* Access Controls */}
            <div className="digital-section" style={{ marginBottom: '24px' }}>
              <h4 className="subsection-title" style={{ marginBottom: '16px', fontSize: '15px', fontWeight: '600', color: '#374151' }}>
                Access Controls
              </h4>

              <div className="form-row">
                <div className="form-group">
                  <label>Max Downloads</label>
                  <Input
                    type="number"
                    value={digitalSettings.maxDownloads || ''}
                    onChange={(e) => setDigitalSettings({
                      ...digitalSettings,
                      maxDownloads: e.target.value ? parseInt(e.target.value) : null
                    })}
                    placeholder="Unlimited"
                    min="1"
                  />
                  <small className="field-hint">Leave empty for unlimited downloads</small>
                </div>

                <div className="form-group">
                  <label>Link Expiration (days)</label>
                  <Input
                    type="number"
                    value={digitalSettings.expirationDays || ''}
                    onChange={(e) => setDigitalSettings({
                      ...digitalSettings,
                      expirationDays: e.target.value ? parseInt(e.target.value) : null
                    })}
                    placeholder="Never expires"
                    min="1"
                  />
                  <small className="field-hint">Leave empty for links that never expire</small>
                </div>
              </div>
            </div>

            {/* Additional Options */}
            <div className="digital-section" style={{ marginBottom: '24px' }}>
              <h4 className="subsection-title" style={{ marginBottom: '16px', fontSize: '15px', fontWeight: '600', color: '#374151' }}>
                Additional Options
              </h4>

              <div className="form-group" style={{ padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                <label className="checkbox-label" style={{ marginBottom: '8px' }}>
                  <input
                    type="checkbox"
                    checked={digitalSettings.requiresLicenseKey}
                    onChange={(e) => setDigitalSettings({
                      ...digitalSettings,
                      requiresLicenseKey: e.target.checked
                    })}
                  />
                  <span style={{ fontWeight: '500', fontSize: '14px' }}>Generate License Key</span>
                </label>
                <small className="field-hint" style={{ display: 'block', marginTop: '8px', marginLeft: '24px', color: '#6b7280' }}>
                  Automatically generate a unique license key (XXXX-XXXX-XXXX-XXXX) for each purchase.
                  Useful for software products.
                </small>
              </div>

              <div className="form-group" style={{ marginTop: '16px' }}>
                <label>Delivery Method</label>
                <select
                  className="form-select"
                  value={digitalSettings.deliveryMethod || 'email'}
                  onChange={(e) => setDigitalSettings({
                    ...digitalSettings,
                    deliveryMethod: e.target.value
                  })}
                >
                  <option value="email">Email Delivery (Recommended)</option>
                  <option value="instant">Instant Download (Redirect after payment)</option>
                </select>
                <small className="field-hint">
                  Email delivery sends a branded email with download links. Instant redirects to the download page immediately.
                </small>
              </div>
            </div>

            {/* Preview */}
            {(digitalSettings.externalUrl || digitalSettings.downloadUrl) && (
              <div style={{ padding: '16px', backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                <p style={{ margin: 0, fontSize: '14px', color: '#166534' }}>
                  <strong>Ready for delivery!</strong> Customers will receive a download link after payment.
                  {digitalSettings.maxDownloads && ` Limited to ${digitalSettings.maxDownloads} downloads.`}
                  {digitalSettings.expirationDays && ` Link expires after ${digitalSettings.expirationDays} days.`}
                  {digitalSettings.requiresLicenseKey && ' A license key will be generated.'}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Appointments Tab */}
        {sectionVisible('appointments') && productType === 'appointment' && (
          <div className="tab-content appointments-settings" id="pfs-section-appointments" data-pfs-section="appointments">
            <h3 className="section-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              Appointment Configuration
            </h3>

            {/* Basic Settings */}
            <div className="appointment-section">
              <h4 className="subsection-title">Basic Settings</h4>

              {/* Free/Paid Toggle */}
              <div className="form-group" style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                <label className="checkbox-label" style={{ marginBottom: '8px' }}>
                  <input
                    type="checkbox"
                    checked={isFreeAppointment}
                    onChange={(e) => {
                      setIsFreeAppointment(e.target.checked);
                      if (e.target.checked) {
                        setPrice('0');
                      }
                    }}
                  />
                  <span style={{ fontWeight: '600', fontSize: '14px' }}>This is a free appointment (no payment required)</span>
                </label>
                <small className="field-hint" style={{ display: 'block', marginTop: '8px', marginLeft: '24px', color: '#6b7280' }}>
                  {isFreeAppointment
                    ? '✓ Customers can book this appointment without payment. The price will be set to ₦0.'
                    : '💳 Customers will be required to pay when booking this appointment.'}
                </small>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Duration (minutes) *</label>
                  <Input
                    type="number"
                    value={appointmentSettings.duration}
                    onChange={(e) => setAppointmentSettings({
                      ...appointmentSettings,
                      duration: parseInt(e.target.value) || 60
                    })}
                    placeholder="60"
                    min="15"
                    step="15"
                  />
                  <small className="field-hint">How long each appointment lasts</small>
                </div>

                <div className="form-group">
                  <label>Buffer Time (minutes)</label>
                  <Input
                    type="number"
                    value={appointmentSettings.bufferTime}
                    onChange={(e) => setAppointmentSettings({
                      ...appointmentSettings,
                      bufferTime: parseInt(e.target.value) || 0
                    })}
                    placeholder="15"
                    min="0"
                    step="5"
                  />
                  <small className="field-hint">Time between appointments</small>
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={appointmentSettings.requiresApproval}
                    onChange={(e) => setAppointmentSettings({
                      ...appointmentSettings,
                      requiresApproval: e.target.checked
                    })}
                  />
                  <span>Require manual approval for bookings</span>
                </label>
              </div>

              <div className="form-group">
                <label>Meeting Location</label>
                <Input
                  type="text"
                  value={appointmentSettings.location || ''}
                  onChange={(e) => setAppointmentSettings({
                    ...appointmentSettings,
                    location: e.target.value
                  })}
                  placeholder="e.g., https://zoom.us/j/123456 or 123 Main St, Suite 100"
                />
                <small className="field-hint">
                  Enter a Zoom/Google Meet link or physical address. This will be included in confirmation emails.
                </small>
              </div>
            </div>

            {/* Availability Schedule */}
            <div className="appointment-section">
              <h4 className="subsection-title">Availability Schedule</h4>

              <div className="days-selector">
                <label>Available Days</label>
                <div className="days-grid">
                  {daysOfWeek.map(day => (
                    <label key={day} className="day-checkbox">
                      <input
                        type="checkbox"
                        checked={appointmentSettings.availableDays.includes(day)}
                        onChange={() => handleDayToggle(day)}
                      />
                      <span>{day.charAt(0).toUpperCase() + day.slice(1)}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Time Slots */}
              <div className="time-slots-section">
                <label>Time Slots</label>
                {appointmentSettings.availableTimeSlots.map((slot, index) => (
                  <div key={index} className="time-slot-item">
                    <span className="slot-day">{slot.day.charAt(0).toUpperCase() + slot.day.slice(1)}</span>
                    <span className="slot-time">{slot.start} - {slot.end}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveTimeSlot(index)}
                      className="remove-slot-btn"
                    >
                      ✕
                    </button>
                  </div>
                ))}

                {appointmentSettings.availableDays.length > 0 && (
                  <div className="add-time-slot">
                    <select
                      id="slot-day-select"
                      className="slot-day-select"
                      defaultValue=""
                    >
                      <option value="" disabled>Select day</option>
                      {appointmentSettings.availableDays.map(day => (
                        <option key={day} value={day}>
                          {day.charAt(0).toUpperCase() + day.slice(1)}
                        </option>
                      ))}
                    </select>
                    <input
                      type="time"
                      id="slot-start-time"
                      className="slot-time-input"
                      defaultValue="09:00"
                    />
                    <span>to</span>
                    <input
                      type="time"
                      id="slot-end-time"
                      className="slot-time-input"
                      defaultValue="17:00"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const day = document.getElementById('slot-day-select').value;
                        const start = document.getElementById('slot-start-time').value;
                        const end = document.getElementById('slot-end-time').value;
                        if (day && start && end) {
                          handleAddTimeSlot(day, start, end);
                        }
                      }}
                      className="add-slot-btn"
                    >
                      ➕ Add Time Slot
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Cancellation Policy */}
            <div className="appointment-section">
              <h4 className="subsection-title">Cancellation Policy</h4>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={appointmentSettings.cancellationPolicy.allowCancellation}
                    onChange={(e) => setAppointmentSettings({
                      ...appointmentSettings,
                      cancellationPolicy: {
                        ...appointmentSettings.cancellationPolicy,
                        allowCancellation: e.target.checked
                      }
                    })}
                  />
                  <span>Allow customers to cancel appointments</span>
                </label>
              </div>

              {appointmentSettings.cancellationPolicy.allowCancellation && (
                <div className="form-group">
                  <label>Minimum Notice (hours)</label>
                  <Input
                    type="number"
                    value={appointmentSettings.cancellationPolicy.minHoursNotice}
                    onChange={(e) => setAppointmentSettings({
                      ...appointmentSettings,
                      cancellationPolicy: {
                        ...appointmentSettings.cancellationPolicy,
                        minHoursNotice: parseInt(e.target.value) || 24
                      }
                    })}
                    placeholder="24"
                    min="1"
                  />
                  <small className="field-hint">Hours before appointment that cancellation is allowed</small>
                </div>
              )}
            </div>

            {/* Calendar Integration */}
            <div className="appointment-section calendar-integration">
              <div style={{
                padding: '16px',
                backgroundColor: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '16px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                    <line x1="16" y1="2" x2="16" y2="6"/>
                    <line x1="8" y1="2" x2="8" y2="6"/>
                    <line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                  <span style={{ fontSize: '14px', color: '#374151' }}>
                    Integrate Google Calendar in <strong>Settings → Integrations</strong>
                  </span>
                </div>
                <a
                  href="/settings?tab=integrations"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 14px',
                    backgroundColor: '#0D7463',
                    color: 'white',
                    textDecoration: 'none',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '500',
                    whiteSpace: 'nowrap'
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    window.location.href = '/settings?tab=integrations';
                  }}
                >
                  Go to Settings
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12"/>
                    <polyline points="12 5 19 12 12 19"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Form Actions — only rendered in legacy tabs layout. In sections
            layout, the page-level sticky topbar owns the Publish CTA. */}
        {!isSections && (
          <div className="form-actions">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={loading || uploading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || uploading || !name.trim() || (productType !== 'appointment' && !price) || (productType === 'appointment' && !isFreeAppointment && !price)}
            >
              {uploading ? 'Uploading...' :
               loading ? 'Saving...' :
               (product ? 'Update Product' : 'Create Product')}
            </Button>
          </div>
        )}
      </form>

      {/* Variant Builder Drawer — sections layout only.
          Bottom sheet on mobile, right-side drawer on desktop. */}
      {isSections && (
        <VariantBuilderDrawer
          open={variantDrawerOpen}
          onClose={() => setVariantDrawerOpen(false)}
          value={variantMatrix}
          onChange={handleVariantMatrixChange}
          basePrice={parseFloat(price) || 0}
          baseSku={sku || ''}
          attributeLimit={attributeLimit}
          disabled={loading}
        />
      )}

      {/* Phase 4.A.6 — Receive new stock sidebar. Available only when
          editing an existing product (during create, the initial
          quantity input creates an OPENING_STOCK batch on save). */}
      {isEditingExisting && (
        <ReceiveStockSidebar
          isOpen={receiveStockOpen}
          onClose={() => setReceiveStockOpen(false)}
          product={product}
          defaultCostPerUnit={costPrice}
          defaultExpiryDate={expiryDate}
          onSuccess={({
            newQuantity,
            priceUpdated,
            appliedSellPrice,
            costPriceUpdated,
            newCostPrice,
            compareAtPriceCleared,
          }) => {
            if (Number.isFinite(newQuantity)) setDisplayQuantity(newQuantity);
            // Mirror sell price into the form state so a tab switch
            // doesn't show the stale ₦2,500 still sitting in the input.
            if (priceUpdated && Number.isFinite(appliedSellPrice)) {
              setPrice(String(appliedSellPrice));
            }
            // Latest-cost pattern: when the merchant entered a new
            // unit cost, backend stamps it on Product.costPrice so
            // the Margin display reflects what new sales will cost.
            if (costPriceUpdated && Number.isFinite(newCostPrice)) {
              setCostPrice(String(newCostPrice));
            }
            // If we cleared the stale compare-at, sync the form field
            // too so the merchant can re-enter a real "was" price.
            if (compareAtPriceCleared) {
              setCompareAtPrice('');
            }
          }}
        />
      )}

      {/* Media Library Modal */}
      <Modal
        isOpen={showMediaLibrary}
        onClose={() => setShowMediaLibrary(false)}
        title="Select Media"
        size="xl"
      >
        <MediaLibrarySelector
          onSelect={(selectedMedia) => {
            if (mediaType === 'image') {
              setImageUrl(selectedMedia[0]?.url || '');
            } else {
              const urls = selectedMedia.map(m => m.url);
              setImages(urls.join(', '));
            }
            setShowMediaLibrary(false);
          }}
          type={mediaType}
        />
      </Modal>
    </div>
  );
};

// Media Library Selector Component
// Fetches its own paginated page from /media so it can walk the entire
// library without being capped by whatever the parent screen has cached
// in Redux (which was previously stuck at the first 20 items).
const MediaLibrarySelector = ({ onSelect, type }) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [pageInfo, setPageInfo] = useState({ pages: 1, total: 0 });
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const LIMIT = 24;

  // Debounce the search input so we don't hammer the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearchTerm(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setFetchError(null);
      try {
        const params = new URLSearchParams({
          type: 'image',
          page: String(page),
          limit: String(LIMIT)
        });
        if (searchTerm) params.set('search', searchTerm);
        const { data } = await axios.get(`/media?${params.toString()}`);
        if (cancelled) return;
        setItems(Array.isArray(data?.media) ? data.media : []);
        setPageInfo({
          pages: data?.pagination?.pages || 1,
          total: data?.pagination?.total || 0
        });
      } catch (err) {
        if (cancelled) return;
        setFetchError(err?.response?.data?.error || err?.message || 'Failed to load media');
        setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [page, searchTerm]);

  const handleSelect = (item) => {
    if (type === 'image') {
      setSelectedItems([item]);
    } else {
      const isSelected = selectedItems.find(selected => selected.id === item.id);
      if (isSelected) {
        setSelectedItems(selectedItems.filter(selected => selected.id !== item.id));
      } else {
        setSelectedItems([...selectedItems, item]);
      }
    }
  };

  const goPrev = () => setPage(p => Math.max(1, p - 1));
  const goNext = () => setPage(p => Math.min(pageInfo.pages, p + 1));

  return (
    <div className="media-library-modal">
      <div className="media-library-search">
        <input
          type="text"
          placeholder="Search media..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="media-search-input"
        />
      </div>

      <div className="media-library-grid">
        {loading ? (
          <div style={{
            gridColumn: '1 / -1',
            textAlign: 'center',
            padding: '40px',
            color: '#6b7280',
            fontSize: '14px'
          }}>
            Loading…
          </div>
        ) : fetchError ? (
          <div style={{
            gridColumn: '1 / -1',
            textAlign: 'center',
            padding: '40px',
            color: '#b91c1c',
            fontSize: '14px'
          }}>
            {fetchError}
          </div>
        ) : items.length > 0 ? (
          items.map(item => {
            const isSelected = selectedItems.find(selected => selected.id === item.id);
            return (
              <div
                key={item.id}
                onClick={() => handleSelect(item)}
                className={`media-library-item ${isSelected ? 'selected' : ''}`}
              >
                <img
                  src={getImageUrl(item.url)}
                  alt={item.alt || item.filename}
                  className="media-library-image"
                />
                {isSelected && <div className="media-library-check">✓</div>}
              </div>
            );
          })
        ) : (
          <div style={{
            gridColumn: '1 / -1',
            textAlign: 'center',
            padding: '40px',
            color: '#6b7280',
            fontSize: '14px'
          }}>
            {searchTerm
              ? `No images match "${searchTerm}".`
              : 'No images found in library. Upload images from the Media page.'}
          </div>
        )}
      </div>

      {pageInfo.pages > 1 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            padding: '10px 0',
            fontSize: '13px',
            color: '#4b5563'
          }}
        >
          <Button variant="outline" onClick={goPrev} disabled={page <= 1 || loading}>
            Previous
          </Button>
          <span>
            Page <strong>{page}</strong> of <strong>{pageInfo.pages}</strong>
            {pageInfo.total ? <span style={{ color: '#9ca3af' }}> · {pageInfo.total} items</span> : null}
          </span>
          <Button variant="outline" onClick={goNext} disabled={page >= pageInfo.pages || loading}>
            Next
          </Button>
        </div>
      )}

      <div className="media-library-actions">
        <span className="media-library-count">{selectedItems.length} selected</span>
        <div className="media-library-buttons">
          <Button variant="outline" onClick={() => onSelect([])}>
            Cancel
          </Button>
          <Button onClick={() => onSelect(selectedItems)} disabled={selectedItems.length === 0}>
            Select
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProductForm;
